import React from "react";
import { withStyles } from "@material-ui/core/styles";
import {
  Grid,
  MenuItem,
  TextField,
  IconButton,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import PropTypes from "prop-types";
import uuid from "react-uuid";

import { filterTemplateName } from "../../../utils/templateUtils";
import { fieldModel, typesList } from "./common";
import ValueField from "./ValueField";

const styles = () => ({
  collapseItem: {
    width: 50,
  },
  wrapper: {
    margin: 5,
  },
  rowItem: {
    margin: "5px 0",
  },
  fieldName: {
    width: 300,
  },
  typeSelector: {
    width: 100,
  },
  fieldValue: {
    width: 300,
  },
});

const TemplateField = ({
  currentNode,
  onChange,
  onDelete,
  templateIndex,
  setTemplates,
  templates,
  setError,
  setIsChange,
  setIsHideNestedItems,
  isHideNestedItems,
  renderExpandButton = false,
  classes,
}) => {
  const { name, type, value, id } = currentNode;
  const onChangeName = ({ newValue }) => {
    onChange({
      name: { ...name, value: filterTemplateName(newValue) },
      type,
      value,
      templateIndex,
      nodeId: id,
    });
  };

  const onChangeType = ({ newValue }) => {
    let defaultValue = "";
    switch (newValue) {
      case "number":
        defaultValue = 1;
        break;
      case "object":
        defaultValue = [fieldModel(uuid())];
        break;
      case "boolean":
        defaultValue = true;
        break;
      default:
        defaultValue = "value";
    }

    onChange({
      name,
      type: { ...type, value: newValue },
      value: { ...value, value: defaultValue },
      templateIndex,
      nodeId: id,
    });
  };

  const onChangeValue = ({ newValue }) => {
    newValue !== "object" &&
      onChange({
        name,
        type,
        value: { ...value, value: newValue },
        templateIndex,
        nodeId: id,
      });
  };

  const addNestedField = (nodeIdToAddField) => {
    (function recursiveSearch(templateFields) {

      for (let i = 0; i < templateFields.length; i++) {
        if (templateFields[i].id === nodeIdToAddField) {

          const name = `field_${templateFields[i].value.value.length}`;
          const nameError =
            templateFields[i].value.value.filter(
              (data) => data.name.value === name
            ).length > 0;

          if (nameError) {
            setError(true);
          }

          templateFields[i].value.value.push({
            id: uuid(),
            name: {
              value: name,
              error: nameError,
            },
            type: {
              value: "string",
              error: false,
            },
            value: {
              value: "value",
              error: false,
            },
          });

          return null;
        }

        if (Array.isArray(templateFields[i]?.value.value)) {
          recursiveSearch(templateFields[i].value.value);
        }
      }

    })(templates[templateIndex].fields);
    setTemplates([...templates]);
    setIsChange(true);
  };

  return (
    <div className={classes.wrapper}>
      <Grid container>
        <Grid className={classes.rowItem} item xs={type.value === "object" ? 8 : 11}>
          <Grid container spacing={2}>
            {/* collapse icon */}
            <Grid item className={classes.collapseItem}>
              {renderExpandButton && (
                <IconButton
                  onClick={() => setIsHideNestedItems(!isHideNestedItems)}
                >
                  {isHideNestedItems ? (
                    <KeyboardArrowRightIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )}
                </IconButton>
              )}
            </Grid>
            {/* name */}
            <Grid item className={classes.fieldName}>
              <TextField
                fullWidth
                required
                variant="outlined"
                label="Name"
                error={name.error}
                value={name.value}
                onChange={(e) => onChangeName({ newValue: e.target.value })}
              />
              {name.error && (
                <Typography color="error">
                  The fields is required and must be unique
                </Typography>
              )}
            </Grid>
            {/* type */}
            <Grid item className={classes.typeSelector}>
              <TextField
                fullWidth
                required
                select
                f="outlined"
                label="Type"
                value={type.value}
                onChange={(e) => {
                  onChangeType({ newValue: e.target.value });
                }}
              >
                {typesList.map(({ name, value }) => (
                  <MenuItem key={value} value={value}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* value */}
            {type.value !== "object" && (
              <Grid item  className={classes.fieldValue}>
                <ValueField
                  currentNode={currentNode}
                  onChangeValue={onChangeValue}
                  addNestedField={addNestedField}
                  parentName={name.value}
                />
                {value.error && type !== "object" && (
                  <Typography color="error">The fields is required!</Typography>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid className={classes.rowItem} item xs={type.value === "object" ? 4 : 1}>
          <Grid container justifyContent="flex-end">
            {/* value */}
            {type.value === "object" && (
              <Grid justifyContent="flex-end" container item xs={10}>
                <ValueField
                  currentNode={currentNode}
                  onChangeValue={onChangeValue}
                  addNestedField={addNestedField}
                  parentName={name.value}
                />
                {value.error && type !== "object" && (
                  <Typography color="error">The fields is required!</Typography>
                )}
              </Grid>
            )}
            {/* delete icon */}
            <Grid item>
              <IconButton
                onClick={() => onDelete({ idToDelete: id, templateIndex })}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

TemplateField.propTypes = {
  name: PropTypes.shape({
    value: PropTypes.string.isRequired,
    error: PropTypes.bool.isRequired,
  }),
  type: PropTypes.shape({
    value: PropTypes.oneOf(["string", "boolean", "number", "object"])
      .isRequired,
    error: PropTypes.bool.isRequired,
  }),
  value: PropTypes.shape({
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.object,
      PropTypes.bool,
    ]).isRequired,
    error: PropTypes.bool.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  templateIndex: PropTypes.number.isRequired,
};

export default withStyles(styles)(TemplateField);
